import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, Twitch, Radio, Youtube, Music2, Send, Loader2,
         Heart, Star, Gem, Banknote } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 1500;
const EVENTS_POLL_MS = 4000;
const MAX_ROWS = 80;

const SOURCE_META = {
  twitch:  { Icon: Twitch,  color: '#a78bfa', label: 'Twitch' },
  kick:    { Icon: Radio,   color: '#22c55e', label: 'Kick' },
  youtube: { Icon: Youtube, color: '#ef4444', label: 'YouTube' },
  tiktok:  { Icon: Music2,  color: '#ff2b7e', label: 'TikTok' },
};

// Compact inline-row meta for community + platform events.
const EVENT_META = {
  follow:          { Icon: Heart,    accent: '#f472b6', label: 'heeft zich aangesloten bij de community' },
  sub:             { Icon: Star,     accent: '#fbbf24', label: 'subscribed' },
  bits:            { Icon: Gem,      accent: '#22d3ee', label: 'cheered' },
  tip:             { Icon: Banknote, accent: '#34d399', label: 'doneerde' },
  yt_superchat:    { Icon: Banknote, accent: '#f97316', label: 'SuperChat' },
  yt_supersticker: { Icon: Gem,      accent: '#fb923c', label: 'SuperSticker' },
  yt_member:       { Icon: Star,     accent: '#dc2626', label: 'lid geworden' },
  kick_sub:        { Icon: Star,     accent: '#16a34a', label: 'subscribed' },
  kick_gift:       { Icon: Gem,      accent: '#65a30d', label: 'gifted subs' },
  tiktok_gift:     { Icon: Gem,      accent: '#ff2b7e', label: 'TikTok gift' },
};

/**
 * Homepage multi-chat panel — same visual shell as the previous Twitch
 * iframe (rounded-2xl, violet header with MessageCircle icon + popout link)
 * but powered by the unified aggregator so viewers see Twitch + Kick +
 * YouTube chats in one place.
 */
export default function HomeMultiChat({ broadcasterLogin }) {
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [canSend, setCanSend] = useState(false);
  const [sendReason, setSendReason] = useState('not_authenticated');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const seqRef = useRef(0);
  const lastAlertTsRef = useRef(0);
  const palertSeqRef = useRef(0);
  const seenEventIdsRef = useRef(new Set());
  const scrollRef = useRef(null);
  const stuckToBottomRef = useRef(true);
  const { user, loginTwitch } = useAuth();

  // Poll for new messages
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await fetch(
          `${BACKEND}/api/multichat/messages?after=${seqRef.current}&limit=100`
        );
        const d = await r.json();
        if (!alive) return;
        const fresh = d?.messages || [];
        if (fresh.length > 0) {
          seqRef.current = fresh[fresh.length - 1].seq;
          setMessages((prev) => {
            const merged = [...prev, ...fresh];
            return merged.slice(-MAX_ROWS);
          });
        }
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Poll community + platform events → inline event rows.
  useEffect(() => {
    let alive = true;
    let isFirstFetch = true;
    const tick = async () => {
      try {
        const url = new URL(`${BACKEND}/api/multichat/events`);
        url.searchParams.set('after_alerts', String(lastAlertTsRef.current));
        url.searchParams.set('after_palerts', String(palertSeqRef.current));
        const r = await fetch(url.toString());
        const d = await r.json();
        if (!alive) return;

        // First fetch: only seed the "seen" watermarks so we skip any old
        // buffered events (subs/bits/donations from previous streams).
        // The feed should reflect what's happening now — not history.
        if (isFirstFetch) {
          isFirstFetch = false;
          for (const a of d?.alerts || []) {
            seenEventIdsRef.current.add(a.id);
            lastAlertTsRef.current = Math.max(lastAlertTsRef.current, a.ts || 0);
          }
          for (const a of d?.platform_alerts || []) {
            palertSeqRef.current = Math.max(palertSeqRef.current, a.seq || 0);
          }
          return;
        }

        const fresh = [];
        for (const a of d?.alerts || []) {
          if (seenEventIdsRef.current.has(a.id)) continue;
          seenEventIdsRef.current.add(a.id);
          lastAlertTsRef.current = Math.max(lastAlertTsRef.current, a.ts || 0);
          fresh.push({ ...a, _sortTs: a.ts || 0 });
        }
        for (const a of d?.platform_alerts || []) {
          palertSeqRef.current = Math.max(palertSeqRef.current, a.seq || 0);
          fresh.push({ ...a, _sortTs: a.ts || 0 });
        }
        if (fresh.length > 0) {
          setEvents((prev) => {
            const merged = [...prev, ...fresh].sort((a, b) => a._sortTs - b._sortTs);
            return merged.slice(-40);
          });
        }
      } catch { /* silent */ }
    };
    tick();
    const id = setInterval(tick, EVENTS_POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const visibleRows = useMemo(() => {
    const chatRows = messages.map((m) => ({
      _kind: 'chat', _key: `chat:${m.seq}`, _sortTs: m.ts || 0, ...m,
    }));
    const eventRows = events.map((e) => ({
      _kind: 'event', _key: e.id, _sortTs: e._sortTs || e.ts || 0, ...e,
    }));
    return [...chatRows, ...eventRows]
      .sort((a, b) => a._sortTs - b._sortTs)
      .slice(-MAX_ROWS);
  }, [messages, events]);

  // Auto-scroll to bottom when new messages arrive, unless the user
  // scrolled up (then we leave them alone).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stuckToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [visibleRows]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    stuckToBottomRef.current = nearBottom;
  };

  // Ask backend whether the current viewer can send chat messages.
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const r = await api.get('/multichat/send/status');
        if (!alive) return;
        setCanSend(!!r.data?.can_send);
        setSendReason(r.data?.reason || '');
      } catch {
        if (alive) setCanSend(false);
      }
    };
    check();
    return () => { alive = false; };
  }, [user?.id]);

  const sendMessage = async () => {
    const msg = draft.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      const r = await api.post('/multichat/send', { message: msg });
      if (r.data?.drop_reason) {
        toast({
          title: 'Twitch weigerde je bericht',
          description: r.data.drop_reason,
          variant: 'destructive',
        });
      } else {
        setDraft('');
      }
    } catch (err) {
      const code = err?.response?.status;
      if (code === 428) {
        setCanSend(false);
        setSendReason('reconnect_required');
        toast({
          title: 'Verbind je Twitch account opnieuw',
          description:
            'We hebben je toestemming nodig om berichten te sturen.',
        });
      } else if (code === 401) {
        toast({ title: 'Log eerst in', variant: 'destructive' });
      } else {
        toast({
          title: 'Bericht niet verzonden',
          description: err?.response?.data?.detail || 'Onbekende fout',
          variant: 'destructive',
        });
      }
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div
      className="mt-4 flex flex-col min-h-[440px] lg:min-h-0 lg:flex-1 rounded-2xl overflow-hidden border border-white/10 bg-zinc-900/60 shadow-2xl shadow-violet-900/30"
      data-testid="hero-multi-chat"
    >
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/[0.03]">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-violet-300" />
          <span className="text-sm font-semibold text-white">
            Multi-chat
          </span>
          <span className="hidden sm:flex items-center gap-1 ml-1">
            {Object.entries(SOURCE_META).map(([k, m]) => {
              const Icon = m.Icon;
              return (
                <span
                  key={k}
                  className="w-4 h-4 rounded flex items-center justify-center"
                  style={{
                    background: `${m.color}22`,
                    color: m.color,
                    boxShadow: `0 0 6px ${m.color}55`,
                  }}
                  title={m.label}
                >
                  <Icon className="w-2.5 h-2.5" />
                </span>
              );
            })}
          </span>
        </div>
        <a
          href={`https://www.twitch.tv/popout/${broadcasterLogin}/chat`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-zinc-400 hover:text-white transition-colors"
          data-testid="hero-chat-popout"
        >
          Popout ↗
        </a>
      </div>

      <div className="relative flex-1 min-h-0 overflow-hidden">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="absolute inset-0 overflow-y-auto no-scrollbar bg-zinc-950 px-3 py-3 space-y-1.5"
          data-testid="hero-chat-scroll"
        >
        {visibleRows.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-zinc-500">
            Wachten op berichten uit Twitch, Kick &amp; YouTube…
          </div>
        ) : (
          visibleRows.map((r) => {
            if (r._kind === 'event') {
              const em = EVENT_META[r.kind] || {};
              const EIcon = em.Icon || Star;
              const accent = em.accent || '#f472b6';
              return (
                <div
                  key={r._key}
                  className="flex items-start gap-2 text-sm leading-snug rounded-lg px-2 py-1 hc-row hc-event"
                  data-testid={`hero-event-row-${r.kind}`}
                  style={{
                    background: `${accent}18`,
                    boxShadow: `0 0 0 1px ${accent}66, 0 0 12px ${accent}55`,
                  }}
                >
                  <span
                    className="mt-1 w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${accent}30`,
                      color: accent,
                      boxShadow: `0 0 6px ${accent}66`,
                    }}
                    title={em.label || r.kind}
                  >
                    <EIcon className="w-2.5 h-2.5" />
                  </span>
                  <span className="min-w-0 break-words">
                    <span className="font-bold mr-1" style={{ color: accent }}>
                      {r.username}
                    </span>
                    <span className="text-zinc-200">
                      {em.label || r.kind}
                      {r.detail ? ` — ${r.detail}` : ''}
                    </span>
                  </span>
                </div>
              );
            }
            const meta = SOURCE_META[r.source] || SOURCE_META.twitch;
            const Icon = meta.Icon;
            return (
              <div
                key={r._key}
                className="flex items-start gap-2 text-sm leading-snug rounded-lg px-2 py-1 hover:bg-white/[0.03] hc-row"
                data-testid={`hero-chat-row-${r.source}`}
              >
                <span
                  className="mt-1 w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${meta.color}22`,
                    color: meta.color,
                    boxShadow: `0 0 6px ${meta.color}55`,
                  }}
                  title={meta.label}
                >
                  <Icon className="w-2.5 h-2.5" />
                </span>
                <span className="min-w-0 break-words">
                  <span
                    className="font-bold mr-1"
                    style={{ color: r.color || meta.color }}
                  >
                    {r.username}
                  </span>
                  <span className="text-zinc-300">{r.message}</span>
                </span>
              </div>
            );
          })
        )}
      </div>
      </div>

      {/* ---- Send input ------------------------------------------ */}
      <ChatInput
        user={user}
        canSend={canSend}
        reason={sendReason}
        draft={draft}
        setDraft={setDraft}
        sending={sending}
        onSend={sendMessage}
        onKeyDown={onKeyDown}
        onReconnect={loginTwitch}
      />

      <style>{`
        .hc-row { animation: hc-row-in 220ms ease-out both; }
        @keyframes hc-row-in {
          0%   { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function ChatInput({ user, canSend, reason, draft, setDraft, sending, onSend, onKeyDown, onReconnect }) {
  // Not logged in — invite to log in via the header button
  if (!user) {
    return (
      <div
        className="px-3 py-2 border-t border-white/10 bg-white/[0.03] text-xs text-zinc-400 flex items-center justify-between"
        data-testid="hero-chat-login-prompt"
      >
        <span>Log in om mee te chatten</span>
        <button
          onClick={onReconnect}
          className="rounded-md bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/40 text-violet-100 px-2 py-1 text-[11px] font-semibold"
        >
          Twitch login ↗
        </button>
      </div>
    );
  }

  // Logged in via Google (no Twitch write scope possible) — hide input.
  if ((user.provider || 'twitch') !== 'twitch') {
    return (
      <div
        className="px-3 py-2 border-t border-white/10 bg-white/[0.03] text-xs text-zinc-500"
        data-testid="hero-chat-nontwitch"
      >
        Log in met Twitch om berichten te sturen naar de chat.
      </div>
    );
  }

  // Logged in via Twitch but old session without `user:write:chat` scope.
  if (!canSend && reason === 'reconnect_required') {
    return (
      <div
        className="px-3 py-2 border-t border-white/10 bg-white/[0.03] text-xs text-zinc-400 flex items-center justify-between gap-2"
        data-testid="hero-chat-reconnect"
      >
        <span>Verbind opnieuw voor toestemming om te chatten</span>
        <button
          onClick={onReconnect}
          className="rounded-md bg-violet-500/20 hover:bg-violet-500/30 border border-violet-500/40 text-violet-100 px-2 py-1 text-[11px] font-semibold"
          data-testid="hero-chat-reconnect-btn"
        >
          Verbind Twitch ↗
        </button>
      </div>
    );
  }

  return (
    <div
      className="px-2 py-2 border-t border-white/10 bg-white/[0.03] flex items-center gap-2"
      data-testid="hero-chat-input-wrap"
    >
      <div className="flex-1 relative">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 500))}
          onKeyDown={onKeyDown}
          disabled={sending}
          placeholder="Stuur een bericht naar Twitch chat…"
          maxLength={500}
          className="w-full bg-zinc-900/80 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-violet-400/60 focus:ring-2 focus:ring-violet-500/30 disabled:opacity-60"
          data-testid="hero-chat-input"
        />
        {draft.length > 400 && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-zinc-500 pointer-events-none">
            {500 - draft.length}
          </span>
        )}
      </div>
      <button
        onClick={onSend}
        disabled={sending || !draft.trim()}
        className="rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-2 flex items-center gap-1 text-xs font-bold shadow-lg shadow-fuchsia-500/30"
        data-testid="hero-chat-send"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">Stuur</span>
      </button>
    </div>
  );
}
